import React, { useState, useEffect, useRef } from 'react';
import { Power, Camera, Upload, CheckCircle, XCircle, Settings, Trash2 } from 'lucide-react';
import { useSettings } from '../../App';
import request from '../../api/request';
import { clearByRetentionInitiateApi, clearByRetentionStatusApi } from '../../api/chats';
import OrderAnalysisSettingsModal from './OrderAnalysisSettingsModal';

interface BusinessProfile {
    about: string | null;
    profile_picture_url: string | null;
}

// 客服跟进开关组件
const OrderAnalysisToggle: React.FC = () => {
    const [isEnabled, setIsEnabled] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // 加载当前设置
        const loadSettings = async () => {
            try {
                const response = await request.get('/api/settings');
                setIsEnabled(response.data.orderAnalysisEnabled ?? true);
            } catch (error) {
                console.error('加载客服跟进设置失败:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadSettings();
    }, []);

    const handleToggle = async () => {
        const newValue = !isEnabled;
        setIsEnabled(newValue);

        try {
            await request.put('/api/settings', {
                orderAnalysisEnabled: newValue
            });
        } catch (error) {
            console.error('更新客服跟进设置失败:', error);
            setIsEnabled(!newValue); // 回滚
        }
    };

    if (isLoading) {
        return <div className="w-12 h-6 bg-muted rounded-full animate-pulse" />;
    }

    return (
        <button
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${isEnabled ? 'bg-primary' : 'bg-muted'
                }`}
        >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
            />
        </button>
    );
};

const SettingsView: React.FC = () => {
    const { chatRetention, setChatRetention } = useSettings();

    // 订单分析设置弹窗
    const [orderAnalysisModalOpen, setOrderAnalysisModalOpen] = useState(false);

    // 头像相关状态
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [uploadMessage, setUploadMessage] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 加载 Business Profile
    useEffect(() => {
        const loadProfile = async () => {
            try {
                const response = await request.get('/api/whatsapp/profile');
                setAvatarUrl(response.data.profile_picture_url);
            } catch (error) {
                console.error('加载 Business Profile 失败:', error);
            } finally {
                setIsLoadingProfile(false);
            }
        };
        loadProfile();
    }, []);

    // 处理头像上传
    const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            setUploadStatus('error');
            setUploadMessage('请选择图片文件 (JPG/PNG)');
            return;
        }

        // 验证文件大小 (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadStatus('error');
            setUploadMessage('图片大小不能超过 5MB');
            return;
        }

        setIsUploadingAvatar(true);
        setUploadStatus('idle');
        setUploadMessage('');

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await request.post('/api/whatsapp/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                setAvatarUrl(response.data.profile_picture_url || avatarUrl);
                setUploadStatus('success');
                setUploadMessage('头像更新成功！');
            } else {
                setUploadStatus('error');
                setUploadMessage(response.data.message || '更新失败');
            }
        } catch (error: any) {
            setUploadStatus('error');
            setUploadMessage(error.response?.data?.detail || '上传失败，请重试');
        } finally {
            setIsUploadingAvatar(false);
            // 清除文件输入
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFrontendRestart = () => {
        // console.log("Restarting frontend...");
        alert("正在重启前端...");
        window.location.reload();
    };

    // 按保留期限清理聊天会话（异步 + 轮询）
    const [clearingChat, setClearingChat] = useState(false);
    const [cleanupProgress, setCleanupProgress] = useState<{
        processed: number;
        total: number;
    } | null>(null);
    const [cleanupTaskId, setCleanupTaskId] = useState<string | null>(null);

    const handleClearByRetention = async () => {
        const days = parseInt(chatRetention);
        if (isNaN(days)) {
            alert('请先选择具体的保留天数（非"永久"）');
            return;
        }
        if (!window.confirm(`确定要删除所有用户 ${days} 天前的聊天记录及关联媒体文件吗？\n\n此操作不可撤销。`)) {
            return;
        }

        setClearingChat(true);
        setCleanupProgress(null);

        let pollTimer: ReturnType<typeof setInterval> | null = null;
        let done = false;

        const stopAndCleanup = () => {
            done = true;
            if (pollTimer) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
            setClearingChat(false);
            setCleanupProgress(null);
            setCleanupTaskId(null);
        };

        try {
            // Step 1: 发起清理任务（后端立即返回 202）
            const initResult = await clearByRetentionInitiateApi(days);
            const taskId = initResult.task_id;
            setCleanupTaskId(taskId);

            // 如果已有任务在运行，后端会返回 200 + 已有 task_id
            if (initResult.progress) {
                setCleanupProgress({
                    processed: initResult.progress.processed,
                    total: initResult.progress.total,
                });
            }

            // Step 2: 轮询进度
            pollTimer = setInterval(async () => {
                if (done) return;
                try {
                    const status = await clearByRetentionStatusApi(taskId);
                    setCleanupProgress({
                        processed: status.progress.processed,
                        total: status.progress.total,
                    });

                    if (status.status === 'completed') {
                        stopAndCleanup();
                        alert(
                            `清理完成！\n` +
                            `删除消息 ${status.result.deleted_messages} 条\n` +
                            `删除文件 ${status.result.deleted_files} 个\n` +
                            `截止时间 ${status.result.cutoff_date || ''}`
                        );
                    } else if (status.status === 'failed') {
                        stopAndCleanup();
                        alert(`清理失败：${status.error || '未知错误'}`);
                    }
                } catch (e: any) {
                    stopAndCleanup();
                    alert(`查询进度失败：${e.message || '未知错误'}`);
                }
            }, 2000);

            // 安全兜底：10 分钟后停止轮询
            setTimeout(() => {
                if (!done) {
                    stopAndCleanup();
                    alert('清理任务超时，请稍后检查结果或重新发起');
                }
            }, 600000);

        } catch (e: any) {
            stopAndCleanup();
            alert(`启动清理失败：${e.message || '未知错误'}`);
        }
    };



    return (
        <div className="flex-1 p-4 sm:p-8 overflow-y-auto bg-background">
            <h1 className="text-3xl font-bold mb-8 text-foreground">系统设置</h1>
            <div className="space-y-8 max-w-4xl mx-auto">

                {/* WhatsApp 头像设置 */}
                <div className="bg-card border border-border rounded-lg p-6 animate-slide-in">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Camera className="w-5 h-5 text-whatsapp-green" />
                        WhatsApp 头像
                    </h3>
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        {/* 头像预览 */}
                        <div className="relative group">
                            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border-2 border-border">
                                {isLoadingProfile ? (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                    </div>
                                ) : avatarUrl ? (
                                    <img
                                        src={avatarUrl}
                                        alt="WhatsApp 头像"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                        <Camera className="w-8 h-8" />
                                    </div>
                                )}
                            </div>
                            {/* 悬停遮罩 */}
                            <label
                                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                                <Upload className="w-6 h-6 text-white" />
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    className="hidden"
                                    onChange={handleAvatarUpload}
                                    disabled={isUploadingAvatar}
                                />
                            </label>
                        </div>

                        {/* 上传说明和状态 */}
                        <div className="flex-1 text-center sm:text-left">
                            <p className="text-muted-foreground text-sm mb-2">
                                点击头像或下方按钮更换 WhatsApp Business 头像
                            </p>
                            <p className="text-muted-foreground text-xs mb-3">
                                建议尺寸: 640×640 像素，最大 5MB
                            </p>

                            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${isUploadingAvatar
                                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                                : 'bg-primary text-primary-foreground hover:bg-primary/90'
                                }`}>
                                {isUploadingAvatar ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                        <span>上传中...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        <span>选择图片</span>
                                    </>
                                )}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    className="hidden"
                                    onChange={handleAvatarUpload}
                                    disabled={isUploadingAvatar}
                                />
                            </label>

                            {/* 上传状态提示 */}
                            {uploadStatus !== 'idle' && (
                                <div className={`mt-3 flex items-center gap-2 text-sm ${uploadStatus === 'success' ? 'text-accent' : 'text-destructive'
                                    }`}>
                                    {uploadStatus === 'success' ? (
                                        <CheckCircle className="w-4 h-4" />
                                    ) : (
                                        <XCircle className="w-4 h-4" />
                                    )}
                                    <span>{uploadMessage}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 全局设置 */}
                <div className="bg-card border border-border rounded-lg p-6 animate-slide-in" style={{ animationDelay: '50ms' }}>
                    <h3 className="text-xl font-semibold mb-4">全局设置</h3>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <label htmlFor="chat-retention" className="text-muted-foreground">聊天记录保存期限</label>
                        <div className="flex items-center gap-2">
                            <select
                                id="chat-retention"
                                value={chatRetention}
                                onChange={(e) => setChatRetention(e.target.value as any)}
                                className="bg-input border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
                            >
                                <option value="7">7 天</option>
                                <option value="30">30 天</option>
                                <option value="60">60 天</option>
                                <option value="90">90 天</option>
                                <option value="permanent">永久</option>
                            </select>
                            <button
                                onClick={handleClearByRetention}
                                disabled={clearingChat || chatRetention === 'permanent'}
                                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md transition-colors"
                                title="删除所选时间之前的所有聊天记录及媒体文件"
                            >
                                <Trash2 className="w-4 h-4" />
                                {clearingChat
                                    ? cleanupProgress && cleanupProgress.total > 0
                                        ? `清理中 ${cleanupProgress.processed}/${cleanupProgress.total}...`
                                        : '启动中...'
                                    : '清理会话'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 客服跟进设置 */}
                <div className="bg-card border border-border rounded-lg p-6 animate-slide-in" style={{ animationDelay: '75ms' }}>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-primary" />
                        客服跟进设置
                    </h3>
                    <div className="flex flex-col gap-4">
                        <p className="text-muted-foreground text-sm">
                            配置订单分析智能体的运行时间、客服电话、模板消息等
                        </p>
                        <button
                            onClick={() => setOrderAnalysisModalOpen(true)}
                            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
                        >
                            <Settings className="w-5 h-5" />
                            <span>打开设置</span>
                        </button>
                    </div>
                </div>

                {/* 系统控制 */}
                <div className="bg-card border border-border rounded-lg p-6 animate-slide-in" style={{ animationDelay: '100ms' }}>
                    <h3 className="text-xl font-semibold mb-4">系统控制</h3>
                    <div className="flex flex-col gap-4">
                        <button onClick={handleFrontendRestart} className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-destructive/50 text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-md transition-colors">
                            <Power className="w-5 h-5" />
                            <span>重启前端</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* 订单分析设置弹窗 */}
            <OrderAnalysisSettingsModal
                isOpen={orderAnalysisModalOpen}
                onClose={() => setOrderAnalysisModalOpen(false)}
            />
        </div>
    );
};


export default SettingsView;

